import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Card, Icon, LanguageButton } from '@/components/ui';
import { useAsyncData } from '@/lib/hooks/useAsyncData';
import { toLocale, useI18n, useT } from '@/lib/i18n';

import { fetchShop, type ShopDto } from './api';

export function EntryScreen({ shopSlug }: { shopSlug: string }) {
  const t = useT();
  const { setLocale } = useI18n();
  const [searchParams] = useSearchParams();

  const currentLang = searchParams.get('lang') ?? '';

  const urlLocale = toLocale(currentLang);
  useEffect(() => {
    if (urlLocale) setLocale(urlLocale);
  }, [urlLocale, setLocale]);

  const { data: shop, error, loading } = useAsyncData<ShopDto>(
    shopSlug,
    (signal) => fetchShop(shopSlug, signal),
    t('menu.shopLoadFailed'),
  );

  if (loading) {
    return <EntrySkeleton />;
  }

  if (error || !shop) {
    return (
      <main className="mx-auto max-w-[420px] px-6 py-16">
        <Card pad>
          <div className="flex items-start gap-3">
            <span className="text-amber-600">
              <Icon name="warn" size={20} />
            </span>
            <div>
              <h1 className="text-h3 text-plum-900">{t('entry.shopUnavailable')}</h1>
              <p className="text-body-s mt-1 text-ink-600">{error}</p>
              <p className="text-caption mt-2 text-ink-500">{t('entry.qrHelp')}</p>
            </div>
          </div>
        </Card>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-screen w-full max-w-[420px] lg:grid lg:h-screen lg:min-h-0 lg:max-w-none lg:grid-cols-[minmax(0,1fr)_minmax(0,520px)] lg:overflow-hidden">
      
      <div className="relative flex w-full items-center justify-center overflow-hidden bg-plum-900 lg:h-screen">
        
        
        <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-black/35 px-3 py-1.5 text-[11px] font-semibold text-white backdrop-blur-sm">
            <Icon name="qr" size={13} />
            {t('entry.scannedFromSignage')}
          </span>
        </div>
        {shop.entry_image_url ? (
          <img
            src={shop.entry_image_url}
            alt=""

            className="block h-auto w-full"

            aria-hidden="true"
          />
        ) : null}
        <div
          className="absolute inset-0 lg:hidden"
          style={{
            background:
              'linear-gradient(180deg, rgba(46,31,43,.55) 0%, rgba(46,31,43,.20) 50%, #fff 100%)',
          }}
        />
        
        <div
          className="absolute inset-0 hidden lg:block"
          style={{
            background:
              'linear-gradient(180deg, rgba(46,31,43,.55) 0%, rgba(46,31,43,.10) 40%, rgba(46,31,43,.45) 100%)',
          }}
          aria-hidden="true"
        />
        {shop.headline ? (
          <span className="absolute bottom-6 left-5 rounded-full bg-green-700/95 px-4 py-2 text-[11.5px] font-bold tracking-[0.03em] text-white">
            {shop.headline}
          </span>
        ) : null}
      </div>

      <div className="px-6 pt-2 pb-8 lg:flex lg:flex-col lg:justify-center lg:overflow-y-auto lg:px-12 lg:py-12">
        <p className="text-body font-medium text-ink-500">{t('entry.welcome')}</p>
        <h1 className="text-h1 mt-1.5 text-plum-900">{shop.name}</h1>
        
        {shop.name_ja ? (
          <p className="mt-0.5 text-[15px] text-plum-700">{shop.name_ja}</p>
        ) : null}
        <div className="mt-5 h-[3px] w-11 rounded-[2px] bg-green-500" />

        <h2 className="text-body mt-8 mb-3.5 font-semibold text-ink-700">
          {t('entry.selectLanguage')}
        </h2>

        {shop.languages.length === 0 ? (
          <p className="text-body-s text-ink-500">
            {t('entry.noLanguages')}
          </p>
        ) : (
          <nav aria-label={t('entry.languageNav')}>
            {shop.languages.map((lang) => (
              <LanguageButton
                key={lang.code}
                code={lang.code}
                nativeName={lang.native_name}

                {...(lang.code === currentLang
                  ? {}
                  : { englishName: lang.display_name })}
                active={lang.code === currentLang}

                href={`/shop/${shop.slug}/menu?lang=${lang.code}`}
              />
            ))}
          </nav>
        )}

        <p className="text-micro mt-6 text-center text-ink-500">
          {t('entry.poweredBy')}
        </p>
        <p className="text-micro mt-2 text-center">
          <Link
            to={`/shop/${shop.slug}/menu`}
            className="text-ink-500 underline-offset-2 hover:underline"
          >
            {t('entry.continueWithout')}
          </Link>
        </p>
      </div>
    </main>
  );
}

function EntrySkeleton() {
  const t = useT();
  return (
    <main className="mx-auto max-w-[420px]" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('entry.loading')}</span>
      <div className="h-[270px] w-full animate-pulse bg-plum-100" />
      <div className="px-6 pt-2">
        <div className="h-4 w-24 animate-pulse rounded bg-ink-100" />
        <div className="mt-2 h-8 w-56 animate-pulse rounded bg-ink-100" />
        <div className="mt-8 h-14 w-full animate-pulse rounded-[var(--radius-lg)] bg-ink-100" />
        <div className="mt-3 h-14 w-full animate-pulse rounded-[var(--radius-lg)] bg-ink-100" />
        <div className="mt-3 h-14 w-full animate-pulse rounded-[var(--radius-lg)] bg-ink-100" />
      </div>
    </main>
  );
}
